import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface PendingComment {
  id: string;
  from: number;
  to: number;
}

export const CommentDecorationsExtension = Extension.create({
  name: 'commentDecorations',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('commentDecorations'),
        state: {
          init(_, state) {
            // Initialize with decorations for any existing comment marks in the document
            const decorations: Decoration[] = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText) return;

              node.marks.forEach(mark => {
                if (mark.type.name === 'comment' && mark.attrs.commentId) {
                  decorations.push(
                    Decoration.inline(pos, pos + node.nodeSize, {
                      class: 'tiptap-comment-highlight',
                      'data-comment-id': mark.attrs.commentId,
                    }),
                  );
                }
              });
            });
            return DecorationSet.create(state.doc, decorations);
          },
          apply(tr, oldSet) {
            // Map decorations through document changes
            let newSet = oldSet.map(tr.mapping, tr.doc);

            // Check for add/remove pending comment metadata
            const addPending = tr.getMeta('addPendingComment') as
              | PendingComment
              | undefined;
            if (addPending) {
              const decoration = Decoration.inline(
                addPending.from,
                addPending.to,
                {
                  class: 'tiptap-comment-highlight',
                  'data-comment-id': addPending.id,
                },
                { commentId: addPending.id },
              );
              newSet = newSet.add(tr.doc, [decoration]);
            }

            const removePending = tr.getMeta('removePendingComment') as
              | string
              | undefined;
            if (removePending) {
              newSet = newSet.remove(
                newSet.find(
                  undefined,
                  undefined,
                  spec => spec.commentId === removePending,
                ),
              );
            }

            return newSet;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});
